import { useEffect, useMemo } from 'react';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { SCORE_THRESHOLDS, tokens } from '../../theme-next';
import { countPieces } from './piecesGridCounts';
import { isCtRequired } from '../FormCGIA/validation/ctRequirement';
import { PM_NOT_REQUIRED_DOC_TYPES, isAcheteurPersonneMorale } from '../FormCGIA/validation/personneMorale';
import { resolveHebergementPiSlots, routageIdentiteApplique } from './piecesGridHebergeur';
import { isRecepisseDaRequired } from './piecesGridRequirements';

/**
 * Vue récap par pièce — alternative additive au layout actuel.
 *
 * Inspiré du rendu Claude Desktop utilisé par François : 3 KPIs en tête
 * (Conformes / Points d'attention / Blocages) puis grille 3×N de cards,
 * une par pièce du dossier. Chaque card a son icône statut, son label,
 * et une ligne d'info clé extraite.
 *
 * Affiché en parallèle de la vue InlineFileViewer + FieldEditList pour
 * permettre au relecteur GOD de comparer A/B et de choisir la
 * présentation la plus efficace en relecture.
 */

interface PieceTile {
  docType: string;
  label: string;
  status: 'conforme' | 'attention' | 'blocage' | 'absent';
  /** 1-2 lignes d'info clé (ex : "EH-393-MA · signée · 10/04/2026"). */
  hint?: string | null;
  /** Causes précises du statut (alimentent les tooltips des KPIs). */
  reasons: string[];
}

interface PiecesGridProps {
  extractions: Array<{
    id: number;
    docTypeDetected: string;
    docTypeMatch: boolean;
    confidence: number;
    fields: unknown;
    warnings: unknown;
    /** Pièce d'identité que le routage n'a rattachée à personne (cf. CGReviewPage). */
    identityUnrouted?: boolean;
  }>;
  /** Findings du moteur de cross-check (transmis depuis CGReviewPage). */
  findings?: Array<{
    ruleId: string;
    severity: 'error' | 'warning' | 'info';
    documentTypes?: string[];
    /** Message humain du finding — affiché tel quel dans les tooltips KPI. */
    message?: string;
  }>;
  /**
   * Quand un verdict LLM (moteur v1 / audit Opus) fait autorité, le statut
   * d'une pièce PRÉSENTE est dérivé UNIQUEMENT de ses findings (error →
   * blocage, warning → attention). On n'escalade plus via les heuristiques
   * regex-era `docTypeMismatch` / `lowConfidence` — c'est ce qui garantit
   * l'iso avec FormCGIA (sinon faux blocages : ex. justif_domicile détecté
   * en doublon avec un schéma divergent, ou confiance OCR 97 < seuil 99).
   * Les pièces ABSENTES restent un blocage (doc requis manquant).
   */
  authoritativeFindings?: boolean;
  /**
   * Voir la pièce déposée dans le visualiseur (retour François DY-565-KJ :
   * « quand il y a une remarque, voir qu'une pièce a été déposée et laquelle »).
   * Rend les cartes de pièces PRÉSENTES cliquables → jump viewer.
   */
  onViewDoc?: (docType: string) => void;
  /**
   * Décompte des pièces requises, remonté au parent.
   *
   * `PiecesGrid` est le seul endroit qui sait ce qui est réellement exigé pour
   * CE dossier : il applique le CT non exigible, l'acquéreur personne morale,
   * l'hébergeur, le récépissé du parcours A, le co-titulaire et les versos. Le
   * KPI « Documents » de la fiche affichait à côté `extractions.length / 9`,
   * soit des lignes de table sur un total figé — d'où le « 9/9 » sur un dossier
   * où la carte grise manquait (retour François du 21/08/2026).
   */
  onCountsChange?: (counts: { present: number; required: number }) => void;
  /**
   * Nombre de fichiers déposés pour le co-titulaire acquéreur.
   *
   * Ces pièces sont uploadées SANS OCR par conception : elles n'ont donc aucune
   * ligne d'extraction, et la grille les déclarait « non reçues » alors
   * qu'elles sont au dossier. Le moteur de cross-check connaît déjà ce compte
   * (`cotitulaireAcheteurFilesCount`), la grille l'ignorait.
   */
  cotitulaireFilesCount?: number;
  /**
   * Types de pièces réclamés par une demande encore ouverte, y compris hors
   * liste standard (un Kbis demandé à part, par exemple). Ils font partie de ce
   * que le dossier attend et doivent donc entrer au dénominateur.
   */
  openRequestDocTypes?: string[];
  /**
   * Date de première mise en circulation du service plaque (`info_immat`),
   * source prioritaire pour l'exigibilité du contrôle technique, comme dans
   * FormCGIA. L'OCR de la carte grise reste le fallback : une MEC illisible sur
   * le scan rendait le CT exigible en review et dispensé côté agence, sur le
   * même dossier.
   */
  vehicleDateMec?: string | null;
}

const DOC_LABELS: Record<string, string> = {
  carte_grise: 'Carte grise',
  cerfa_cession: 'Certificat de cession',
  cerfa_demande_immat: "Cerfa demande d'immatriculation",
  mandat: 'Mandat',
  assurance: "Attestation d'assurance",
  controle_technique: 'Contrôle technique',
  piece_identite: "Pièce d'identité du titulaire — RECTO",
  piece_identite_verso: "Pièce d'identité du titulaire — VERSO",
  piece_identite_hebergeur: "Pièce d'identité de l'hébergeur — RECTO",
  piece_identite_hebergeur_verso: "Pièce d'identité de l'hébergeur — VERSO",
  piece_identite_cotitulaire: "Pièce d'identité du co-titulaire — RECTO",
  piece_identite_cotitulaire_verso: "Pièce d'identité du co-titulaire — VERSO",
  permis_conduire: 'Permis de conduire du titulaire — RECTO',
  permis_conduire_verso: 'Permis de conduire du titulaire — VERSO',
  permis_conduire_cotitulaire: 'Permis de conduire du co-titulaire — RECTO',
  permis_conduire_cotitulaire_verso: 'Permis de conduire du co-titulaire — VERSO',
  kbis: 'Kbis',
  kbis_vendeur: 'Kbis du vendeur',
  kbis_acheteur: "Kbis de l'acquéreur",
  justif_domicile: 'Justificatif de domicile',
  attestation_hebergement: "Attestation d'hébergement",
  recepisse_da: "Récépissé de déclaration d'achat",
};

const BASE_REQUIRED_DOCS: string[] = [
  'carte_grise',
  'cerfa_cession',
  'cerfa_demande_immat',
  'mandat',
  'assurance',
  'controle_technique',
  'piece_identite',
  'permis_conduire',
  'justif_domicile',
];

/**
 * Extrait 1-2 valeurs clés d'une extraction OCR pour la ligne d'info
 * (immat + nom titulaire pour la CG, plaque + date pour la cession, etc.).
 * Pure et tolérante aux champs manquants.
 */
const buildHint = (docType: string, fields: unknown): string | null => {
  if (!fields || typeof fields !== 'object') return null;
  const f = fields as Record<string, { value?: unknown } | undefined>;
  const get = (key: string): string | null => {
    const v = f[key]?.value;
    return typeof v === 'string' && v ? v : null;
  };

  switch (docType) {
    case 'carte_grise':
      return [get('immatriculation'), get('titulaire_nom')].filter(Boolean).join(' · ') || null;
    case 'cerfa_cession':
      return [get('immatriculation'), get('vendeur_nom'), get('date_vente')].filter(Boolean).join(' · ') || null;
    case 'cerfa_demande_immat':
      return [get('immatriculation'), get('titulaire_nom')].filter(Boolean).join(' · ') || null;
    case 'mandat':
      return [get('mandataire_nom'), get('mandataire_siret')].filter(Boolean).join(' · ') || null;
    case 'assurance':
      return [get('compagnie'), get('immatriculation'), get('date_effet')].filter(Boolean).join(' · ') || null;
    case 'controle_technique':
      return [get('resultat'), get('date_ct')].filter(Boolean).join(' · ') || null;
    case 'piece_identite':
    case 'piece_identite_verso':
    case 'piece_identite_hebergeur':
    case 'piece_identite_hebergeur_verso':
    case 'piece_identite_cotitulaire':
    case 'piece_identite_cotitulaire_verso':
      return [get('nom'), get('prenom'), get('date_expiration')].filter(Boolean).join(' · ') || null;
    case 'recepisse_da':
      return (
        [get('vendeur_raison_sociale') ?? get('vendeur_nom'), get('date_achat')].filter(Boolean).join(' · ') || null
      );
    case 'permis_conduire':
    case 'permis_conduire_verso':
    case 'permis_conduire_cotitulaire':
    case 'permis_conduire_cotitulaire_verso':
      return [get('nom'), get('prenom'), get('categories')].filter(Boolean).join(' · ') || null;
    case 'justif_domicile':
      return [get('type_document'), get('date_document')].filter(Boolean).join(' · ') || null;
    case 'attestation_hebergement':
      return [get('hebergeur_nom'), get('heberge_nom')].filter(Boolean).join(' · ') || null;
    default:
      return null;
  }
};

export const PiecesGrid = ({
  extractions,
  findings = [],
  authoritativeFindings = false,
  onViewDoc,
  onCountsChange,
  cotitulaireFilesCount = 0,
  openRequestDocTypes,
  vehicleDateMec = null,
}: PiecesGridProps) => {
  const tiles = useMemo<PieceTile[]>(() => {
    // Map docType → meilleure extraction. Tri par confidence, mais à
    // confidence égale on PRÉFÈRE l'extraction dont le type est confirmé
    // (docTypeMatch). Sans ce départage, une pièce déposée en double (ex.
    // justif_domicile lu une fois avec le bon schéma + une fois avec un
    // schéma divergent non confirmé, toutes deux à 97 %) pouvait afficher
    // la variante non confirmée → faux "Type non confirmé / blocage".
    const byDocType = new Map<string, (typeof extractions)[number]>();
    extractions.forEach((e) => {
      // Pièce d'identité que le routage n'a rattachée à PERSONNE : elle
      // n'est la pièce de personne, elle ne peut donc représenter aucune
      // vignette. Elle a son propre bloc « Pièce(s) d'identité à rattacher ».
      if (e.identityUnrouted) return;
      const key = e.docTypeDetected;
      const existing = byDocType.get(key);
      const isBetter =
        !existing ||
        e.confidence > existing.confidence ||
        (e.confidence === existing.confidence && e.docTypeMatch && !existing.docTypeMatch);
      if (isBetter) {
        byDocType.set(key, e);
      }
    });

    // Le routage (personne × type × face) a-t-il parlé sur ce dossier ?
    // Cf. `routageIdentiteApplique` : quand c'est le cas, son verdict prime sur
    // les présomptions d'hébergement ci-dessous.
    const routageApplique = routageIdentiteApplique(extractions);

    // Pattern hébergement, AVANT le routage déterministe : si une attestation
    // d'hébergement est présente ET qu'un fichier multi-doc signale aussi
    // une CNI (warning "contient_aussi_piece_identite"), la CNI uploadée
    // directement EST celle de l'hébergeur — on la déplace dans le slot
    // piece_identite_hebergeur. Le slot piece_identite sera matérialisé
    // ensuite par la boucle multi-doc avec les champs vides.
    //
    // ⚠️ Cette présomption ne vaut QUE tant que rien ne dit à qui est la pièce.
    // Depuis le routage, elle le contredisait : sur FK-371-EZ (test dev du
    // 18/09), la CNI de l'acquéreuse Marine RODRIGUEZ — correctement routée
    // `piece_identite` — était déplacée d'office en vignette « hébergeur »,
    // qui affichait donc SON nom, pendant que la vignette du titulaire restait
    // vide. Seul l'affichage était touché (l'agrégation, elle, suit le
    // routage), mais c'est précisément l'écran où le pôle CG contrôle qui est
    // qui.
    const hasAttestationHeberg = byDocType.has('attestation_hebergement');
    const hasMultiDocPi = extractions.some(
      (e) =>
        e.docTypeMatch && Array.isArray(e.warnings) && e.warnings.some((w) => w === 'contient_aussi_piece_identite'),
    );
    if (!routageApplique && hasAttestationHeberg && hasMultiDocPi && byDocType.has('piece_identite')) {
      const piExtraction = byDocType.get('piece_identite');
      if (piExtraction) {
        byDocType.set('piece_identite_hebergeur', {
          ...piExtraction,
          docTypeDetected: 'piece_identite_hebergeur',
        });
        byDocType.delete('piece_identite');
      }
    }

    // Fichiers multi-documents : si une extraction porte un warning
    // "contient_aussi_<docType>" (cas typique du scan PDF qui regroupe CNI
    // ET permis sur un même fichier), on considère la pièce additionnelle
    // comme reçue. Sinon la PiecesGrid affichait "Permis non reçu" alors
    // que le document est physiquement présent dans le dossier — cas
    // observé DC-39460-6.pdf et DC-39460-7.pdf (CNI + permis combinés).
    extractions.forEach((e) => {
      const warnings: string[] = Array.isArray(e.warnings)
        ? e.warnings.filter((w): w is string => typeof w === 'string')
        : [];
      warnings.forEach((warning) => {
        const match = /^contient_aussi_(.+)$/.exec(warning);
        if (!match) return;
        const extraDocType = match[1];
        if (byDocType.has(extraDocType)) return; // dépôt dédié → on ne supplante pas
        // On clone l'extraction porteuse en réétiquetant le docType : la
        // PiecesGrid considère la pièce comme "reçue" avec la même
        // confiance, et le viewer pointera vers le bon fichier physique.
        byDocType.set(extraDocType, { ...e, docTypeDetected: extraDocType });
      });
    });

    // Pièces optionnelles selon spécificité du dossier (iso form-cgia
    // effectiveDocQueue) : la grille s'allonge dynamiquement quand on
    // détecte un parcours A, un hébergement chez un tiers ou une
    // personne morale acquéreur.
    const fieldsOf = (docType: string): Record<string, { value?: unknown } | undefined> => {
      const ex = byDocType.get(docType);
      if (!ex || !ex.fields || typeof ex.fields !== 'object') return {};
      return ex.fields as Record<string, { value?: unknown } | undefined>;
    };
    const cessionFields = fieldsOf('cerfa_cession');
    // Le Kbis de l'acquéreur : slot routé en priorité, `kbis` brut en repli pour
    // les dossiers antérieurs au dédoublement.
    const kbisFields = { ...fieldsOf('kbis'), ...fieldsOf('kbis_acheteur') };
    // MÊME détection que la checklist agence (FormCGIAv2) et que le récap :
    // 4 signaux, pas 2. La grille ne voyait ni le Kbis déposé ni la raison
    // sociale seule, et déclarait donc « Manquante » le permis et le
    // justificatif de domicile d'une société (EH-214-VH).
    const isPersonneMorale = isAcheteurPersonneMorale({
      flagCession: cessionFields.acheteur_est_personne_morale?.value,
      hasKbis: byDocType.has('kbis_acheteur') || byDocType.has('kbis'),
      acheteurSiret: cessionFields.acheteur_siret?.value ?? kbisFields.siret?.value,
      acheteurRaisonSociale: cessionFields.acheteur_raison_sociale?.value ?? kbisFields.raison_sociale?.value,
    });

    // Hébergement — PI hébergeur déposée en FICHIER DÉDIÉ (cas non couvert par
    // le warning multi-doc ci-dessus) : acquéreur ET hébergeur déposent chacun
    // leur pièce, que cg_ocr_extractions classe en double "piece_identite". On
    // recompose les deux slots (iso recap agence) pour ne pas déclarer la PI de
    // l'hébergeur « MANQUANTE » alors qu'elle est dans le dossier (HH-975-PP).
    //
    // Même réserve que le pattern multi-doc : cette recomposition par prénom ne
    // vaut que tant que le routage ne s'est pas prononcé. Quand il l'a fait et
    // n'a désigné AUCUN hébergeur, c'est un verdict, pas un oubli — la pièce
    // sans personne est déjà remontée dans « Pièce(s) d'identité à rattacher ».
    if (!routageApplique && hasAttestationHeberg && !byDocType.has('piece_identite_hebergeur')) {
      const acheteurPrenom =
        typeof cessionFields.acheteur_prenom?.value === 'string'
          ? (cessionFields.acheteur_prenom.value as string)
          : null;
      const { acquereur, hebergeur } = resolveHebergementPiSlots(extractions, acheteurPrenom);
      if (hebergeur) {
        byDocType.set('piece_identite_hebergeur', { ...hebergeur, docTypeDetected: 'piece_identite_hebergeur' });
        if (acquereur) byDocType.set('piece_identite', { ...acquereur, docTypeDetected: 'piece_identite' });
      }
    }

    // Exigibilité du contrôle technique : MÊME fonction partagée que la règle
    // cross-check `ct_obligatoire_si_mec_4_ans` et la checklist FormCGIAv2. Le
    // CT n'est PAS obligatoire pour un véhicule de moins de 4 ans ou dont le
    // champ X.1 de la CG ("VISITE AVANT LE…") est encore dans le futur — c'est
    // exactement pour ça que FormCGIA ne réclame pas de CT sur ce dossier
    // (GK-008-GD : MEC 2022-10-28 < 4 ans, X.1 valide 2026-10-28). Sans ce
    // partage, la grille marquait le CT "MANQUANTE → blocage" là où FormCGIA
    // n'affiche aucune alerte → divergence d'iso.
    const cgFields = fieldsOf('carte_grise');
    // Service plaque prioritaire, OCR en fallback : même ordre de préférence que
    // `isCtRequiredFromSession` côté agence, pour que les deux écrans tranchent
    // sur la même donnée. (La MEC ne vient pas de `TaxData`, qui ne porte que
    // les taxes, mais de `info_immat`, renseignée pour 98 % des dossiers.)
    const ocrMec =
      typeof cgFields.date_premiere_mec?.value === 'string' ? (cgFields.date_premiere_mec.value as string) : null;
    const ctRequired = isCtRequired({
      dateMecRaw: vehicleDateMec ?? ocrMec,
      dateValiditeCtRaw:
        typeof cgFields.date_validite_ct?.value === 'string' ? (cgFields.date_validite_ct.value as string) : null,
      now: new Date(),
    });

    // CT : présent dans le dossier → on l'affiche (conforme) ; absent mais NON
    // requis → on ne le liste pas (iso FormCGIA) ; absent ET requis → blocage.
    // Acquéreur personne morale : le permis et le justificatif de domicile
    // sortent des pièces requises (une société n'a pas de permis, et le Kbis
    // fait foi pour le domicile via l'adresse de l'établissement). Le Kbis les
    // remplace, ajouté plus bas. Sans ce filtre, la grille comptait deux
    // « Manquante » dans le KPI Blocages là où la checklist agence n'affichait
    // rien — divergence d'iso signalée par François sur EH-214-VH.
    const requiredDocs: string[] = BASE_REQUIRED_DOCS.filter((d) => {
      if (d === 'controle_technique') return byDocType.has('controle_technique') || ctRequired;
      if (isPersonneMorale && (PM_NOT_REQUIRED_DOC_TYPES as readonly string[]).includes(d)) {
        // Pièce quand même déposée par l'agence → on la garde affichée (elle
        // ne sera juste jamais « Manquante »).
        return byDocType.has(d);
      }
      return true;
    });
    if (hasAttestationHeberg) {
      requiredDocs.push('attestation_hebergement');
      requiredDocs.push('piece_identite_hebergeur');
    }
    // Récépissé DA : uploadé OU exigé par le Parcours A. Quand Parcours A est
    // déclenché mais la pièce absente, la règle cross-check
    // `recepisse_da_manquant_parcours_a` fait feu → on matérialise une carte
    // « Manquante » dans la grille, cohérent avec la checklist /form-cgia qui
    // liste déjà la pièce attendue (cas DS-063-CH).
    if (isRecepisseDaRequired(byDocType.has('recepisse_da'), findings)) {
      requiredDocs.push('recepisse_da');
    }
    // Kbis de l'ACQUÉREUR : attendu dès que l'acheteur est une personne morale.
    // `kbis` brut reste la tuile des dossiers antérieurs au dédoublement, pour
    // qu'ils ne perdent pas leur pièce à l'affichage.
    if (isPersonneMorale || byDocType.has('kbis_acheteur')) {
      requiredDocs.push('kbis_acheteur');
    } else if (byDocType.has('kbis')) {
      requiredDocs.push('kbis');
    }
    // Kbis du VENDEUR : jamais exigé (le SIV ne le demande pas), affiché
    // seulement quand il a été déposé.
    if (byDocType.has('kbis_vendeur')) {
      requiredDocs.push('kbis_vendeur');
    }

    // ── Dissociation RECTO/VERSO + co-titulaire (retours François 2026-07-29).
    // Les extractions arrivent avec le slot routé (personne × face) comme type
    // effectif (`routed_doc_type`, migration 0029) : chaque face reçue porte sa
    // propre carte. Une carte VERSO « manquante » n'est matérialisée que sur
    // signal OCR fiable (`est_recto_seul` — CNI/titre de séjour uniquement, un
    // passeport n'a pas de verso et le flag n'existe pas côté permis).
    const insertAfter = (anchor: string, docType: string): void => {
      const idx = requiredDocs.indexOf(anchor);
      requiredDocs.splice(idx >= 0 ? idx + 1 : requiredDocs.length, 0, docType);
    };
    const versoAttendu = (rectoDocType: string): boolean => {
      const recto = fieldsOf(rectoDocType);
      return recto.est_recto_seul?.value === true && recto.type_document?.value !== 'passeport';
    };
    if (byDocType.has('piece_identite_verso') || (byDocType.has('piece_identite') && versoAttendu('piece_identite'))) {
      insertAfter('piece_identite', 'piece_identite_verso');
    }
    if (byDocType.has('permis_conduire_verso')) {
      insertAfter('permis_conduire', 'permis_conduire_verso');
    }
    if (requiredDocs.includes('piece_identite_hebergeur') && byDocType.has('piece_identite_hebergeur_verso')) {
      insertAfter('piece_identite_hebergeur', 'piece_identite_hebergeur_verso');
    }
    // Co-titulaire ACHETEUR : déclaré sur le 13750 ou pièces reçues. Sa PI
    // (recto + verso) est requise (référentiel CTVO §3-ter) ; son permis n'est
    // pas requis mais reste visible s'il a été déposé.
    const cerfaDIFields = fieldsOf('cerfa_demande_immat');
    const cotitulaireDeclare =
      cerfaDIFields.cotitulaire_present?.value === true ||
      (typeof cerfaDIFields.cotitulaire_nom?.value === 'string' &&
        (cerfaDIFields.cotitulaire_nom.value as string).length > 0);
    if (
      cotitulaireDeclare ||
      byDocType.has('piece_identite_cotitulaire') ||
      byDocType.has('piece_identite_cotitulaire_verso')
    ) {
      const piAnchor = requiredDocs.includes('piece_identite_verso') ? 'piece_identite_verso' : 'piece_identite';
      insertAfter(piAnchor, 'piece_identite_cotitulaire');
      if (
        byDocType.has('piece_identite_cotitulaire_verso') ||
        (byDocType.has('piece_identite_cotitulaire') && versoAttendu('piece_identite_cotitulaire'))
      ) {
        insertAfter('piece_identite_cotitulaire', 'piece_identite_cotitulaire_verso');
      }
      if (byDocType.has('permis_conduire_cotitulaire')) requiredDocs.push('permis_conduire_cotitulaire');
      if (byDocType.has('permis_conduire_cotitulaire_verso')) requiredDocs.push('permis_conduire_cotitulaire_verso');
    }

    // Findings groupés par docType : severity max pour le statut, messages
    // conservés par sévérité pour alimenter les tooltips des KPIs. Les INFO sont
    // CONSERVÉES (note de diagnostic pour le vérificateur Skipcar) mais ne font
    // jamais monter le statut d'une pièce — error > warning > info.
    const SEVERITY_RANK: Record<'error' | 'warning' | 'info', number> = { info: 0, warning: 1, error: 2 };
    const docSeverity = new Map<string, 'error' | 'warning' | 'info'>();
    const docFindingMessages = new Map<string, { errors: string[]; warnings: string[]; infos: string[] }>();
    findings.forEach((f) => {
      (f.documentTypes ?? []).forEach((dt) => {
        const current = docSeverity.get(dt);
        if (!current || SEVERITY_RANK[f.severity] > SEVERITY_RANK[current]) {
          docSeverity.set(dt, f.severity);
        }
        const entry = docFindingMessages.get(dt) ?? { errors: [], warnings: [], infos: [] };
        const text = f.message ?? f.ruleId;
        if (f.severity === 'error') entry.errors.push(text);
        else if (f.severity === 'warning') entry.warnings.push(text);
        else entry.infos.push(text);
        docFindingMessages.set(dt, entry);
      });
    });

    // Pièces réclamées par une demande encore ouverte et absentes de la liste
    // standard : elles sont attendues du dossier au même titre que les autres.
    // Sans elles, un Kbis réclamé à part n'apparaissait ni dans la grille ni au
    // dénominateur, alors que c'est lui qui retient le dossier.
    (openRequestDocTypes ?? []).forEach((docType) => {
      if (!requiredDocs.includes(docType)) requiredDocs.push(docType);
    });

    return requiredDocs.map<PieceTile>((docType) => {
      const ex = byDocType.get(docType);
      const label = DOC_LABELS[docType] ?? docType;
      const messages = docFindingMessages.get(docType);
      if (!ex) {
        // Déposée mais non analysable : présente au dossier, simplement pas
        // vérifiée. La compter absente enverrait le vérificateur réclamer une
        // pièce qu'il a déjà sous les yeux.
        if (docType === 'piece_identite_cotitulaire' && cotitulaireFilesCount > 0) {
          return {
            docType,
            label,
            status: 'attention',
            hint: 'Déposée par l’agence, sans analyse automatique — à vérifier à l’œil',
            reasons: ['Pièce déposée hors analyse (co-titulaire)'],
          };
        }
        return {
          docType,
          label,
          status: 'absent',
          hint: "Non reçue — à demander à l'agence",
          // Les findings cross-check qui visent une pièce absente précisent
          // POURQUOI elle est exigée (ex : "Le véhicule a 5.4 ans, un
          // contrôle technique est obligatoire").
          reasons: [
            'Pièce non reçue',
            ...(messages?.errors ?? []),
            ...(messages?.warnings ?? []),
            ...(messages?.infos ?? []),
          ],
        };
      }
      const severity = docSeverity.get(docType);
      const docTypeMismatch = !ex.docTypeMatch;
      const lowConfidence = ex.confidence < SCORE_THRESHOLDS.ORANGE;
      // Quand un verdict LLM fait autorité, seul le finding pilote le statut
      // (iso FormCGIA) ; sinon (filet regex seul) on garde les heuristiques
      // docTypeMismatch / lowConfidence comme signaux d'escalade.
      const status: PieceTile['status'] = authoritativeFindings
        ? severity === 'error'
          ? 'blocage'
          : severity === 'warning'
            ? 'attention'
            : 'conforme'
        : severity === 'error' || docTypeMismatch
          ? 'blocage'
          : severity === 'warning' || lowConfidence
            ? 'attention'
            : 'conforme';

      // Les heuristiques OCR restent affichées en NOTE de tooltip (diagnostic
      // vérificateur) même en mode autorité — elles n'élèvent simplement plus
      // le statut.
      const reasons: string[] = [
        ...(docTypeMismatch ? ["Type de document non confirmé par l'IA"] : []),
        ...(messages?.errors ?? []),
        ...(messages?.warnings ?? []),
        ...(messages?.infos ?? []),
        ...(lowConfidence ? [`Confiance OCR faible (${ex.confidence} %)`] : []),
      ];

      return { docType, label, status, hint: buildHint(docType, ex.fields), reasons };
    });
  }, [extractions, findings, cotitulaireFilesCount, openRequestDocTypes, vehicleDateMec]);

  const kpis = useMemo(() => {
    const describe = (t: PieceTile): string =>
      t.reasons.length > 0 ? `${t.label} — ${t.reasons.join(' · ')}` : t.label;
    const conformeTiles = tiles.filter((t) => t.status === 'conforme');
    const attentionTiles = tiles.filter((t) => t.status === 'attention');
    const blocageTiles = tiles.filter((t) => t.status === 'blocage' || t.status === 'absent');
    return {
      conformes: conformeTiles.length,
      attention: attentionTiles.length,
      blocages: blocageTiles.length,
      conformesDetails: conformeTiles.map((t) => t.label),
      attentionDetails: attentionTiles.map(describe),
      blocagesDetails: blocageTiles.map(describe),
    };
  }, [tiles]);

  // `tiles` porte une ligne par pièce REQUISE pour ce dossier : sa longueur est
  // le dénominateur juste, et tout ce qui n'est pas `absent` a bien été déposé.
  useEffect(() => {
    if (!onCountsChange) return;
    onCountsChange(countPieces(tiles));
  }, [tiles, onCountsChange]);

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius['2xl']}px`,
        boxShadow: tokens.shadow.card,
        padding: '20px 22px',
      }}
    >
      {/* ── Bandeau KPIs ───────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        <Kpi
          label="Pièces conformes"
          value={kpis.conformes}
          color={tokens.color.green}
          icon={<CheckCircleIcon />}
          details={kpis.conformesDetails}
        />
        <Kpi
          label="Points d'attention"
          value={kpis.attention}
          color={tokens.color.orange ?? '#E08A2C'}
          icon={<WarningAmberIcon />}
          details={kpis.attentionDetails}
        />
        <Kpi
          label="Blocages"
          value={kpis.blocages}
          color={tokens.color.red ?? '#EF4444'}
          icon={<BlockIcon />}
          details={kpis.blocagesDetails}
        />
      </Box>

      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.textMuted,
          marginBottom: '12px',
        }}
      >
        Pièces du dossier
      </Typography>

      {/* ── Grille de tuiles ──────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: '10px',
        }}
      >
        {tiles.map((tile) => (
          <PieceTileCard
            key={tile.docType}
            tile={tile}
            onView={onViewDoc && tile.status !== 'absent' ? () => onViewDoc(tile.docType) : undefined}
          />
        ))}
      </Box>
    </Box>
  );
};

const Kpi = ({
  label,
  value,
  color,
  icon,
  details = [],
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  /** Détail par pièce ("Carte grise — Pièce non reçue") affiché en tooltip. */
  details?: string[];
}) => {
  const tooltipContent =
    details.length > 0 ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '2px 0' }}>
        {details.map((d) => (
          // `color: inherit` obligatoire : le variant body1 du thème force un
          // texte sombre → invisible sur le fond sombre du tooltip.
          <Typography key={d} sx={{ fontSize: '12px', lineHeight: 1.45, color: 'inherit' }}>
            • {d}
          </Typography>
        ))}
      </Box>
    ) : (
      ''
    );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          padding: '14px',
          borderRadius: `${tokens.radius.xl}px`,
          backgroundColor: tokens.color.surface,
          border: `0.5px solid ${tokens.color.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: details.length > 0 ? 'help' : 'default',
        }}
      >
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 900,
              color: tokens.color.dark,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: tokens.color.textMuted,
              marginTop: '2px',
              letterSpacing: '0.02em',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const STATUS_COLORS: Record<PieceTile['status'], { icon: string; bg: string; border: string }> = {
  conforme: { icon: tokens.color.green, bg: tokens.color.greenLight, border: tokens.color.greenBorder },
  attention: { icon: tokens.color.orange ?? '#E08A2C', bg: '#FFF7ED', border: '#FED7AA' },
  blocage: {
    icon: tokens.color.red ?? '#EF4444',
    bg: tokens.color.redLight ?? '#FFF5F5',
    border: tokens.color.redBorder ?? '#FECACA',
  },
  // Une pièce manquante compte dans le KPI "Blocages" : elle doit ressortir
  // en rouge comme les blocages, pas en gris discret qu'on ne repère pas.
  absent: {
    icon: tokens.color.red ?? '#EF4444',
    bg: tokens.color.redLight ?? '#FFF5F5',
    border: tokens.color.redBorder ?? '#FECACA',
  },
};

const STATUS_ICON: Record<PieceTile['status'], React.ReactNode> = {
  conforme: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  attention: <WarningAmberIcon sx={{ fontSize: 16 }} />,
  blocage: <BlockIcon sx={{ fontSize: 16 }} />,
  absent: <BlockIcon sx={{ fontSize: 16 }} />,
};

const STATUS_LABEL: Record<PieceTile['status'], string> = {
  conforme: 'Conforme',
  attention: 'À vérifier',
  blocage: 'Blocage',
  absent: 'Manquante',
};

const PieceTileCard = ({ tile, onView }: { tile: PieceTile; onView?: () => void }) => {
  const colors = STATUS_COLORS[tile.status];
  const content = (
    <Box
      onClick={onView}
      title={onView ? 'Voir la pièce déposée' : undefined}
      sx={{
        padding: '12px 14px',
        borderRadius: `${tokens.radius.xl}px`,
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minHeight: 78,
        cursor: onView ? 'pointer' : 'default',
        transition: 'box-shadow 150ms ease, transform 150ms ease',
        ...(onView ? { '&:hover': { boxShadow: tokens.shadow.card, transform: 'translateY(-1px)' } } : {}),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: colors.icon, display: 'flex' }}>{STATUS_ICON[tile.status]}</Box>
        <Typography
          sx={{
            fontSize: '13.5px',
            fontWeight: 800,
            color: tokens.color.dark,
            lineHeight: 1.2,
            flex: 1,
          }}
        >
          {tile.label}
        </Typography>
        <Typography
          sx={{
            fontSize: '10.5px',
            fontWeight: 700,
            color: colors.icon,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {STATUS_LABEL[tile.status]}
        </Typography>
      </Stack>
      {tile.hint && (
        <Typography
          sx={{
            fontSize: '12px',
            color: tokens.color.textSecondary ?? tokens.color.textMuted,
            lineHeight: 1.35,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {tile.hint}
        </Typography>
      )}
    </Box>
  );

  // Tooltip avec la/les cause(s) précise(s) du statut — même contenu que
  // les tooltips des KPIs, mais ciblé sur la pièce survolée.
  if (tile.reasons.length === 0) return content;
  return (
    <Tooltip
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '2px 0' }}>
          {tile.reasons.map((r) => (
            <Typography key={r} sx={{ fontSize: '12px', lineHeight: 1.45, color: 'inherit' }}>
              • {r}
            </Typography>
          ))}
        </Box>
      }
      arrow
      placement="top"
    >
      {content}
    </Tooltip>
  );
};
